import React from 'react';

import {format} from 'date-fns';
import {useGate, useUnit} from 'effector-react';
import {useTranslation} from 'react-i18next';
import {useHistory} from 'react-router-dom';

import {ROUTES} from '@app/routing';
import {EMPTY_VALUE as EMPTY} from '@consts/common';
import {STATUS_COLOR_LIST} from '@consts/statuses';
import {AuditViolationsGate, auditViolationsModel} from '@entities/audit-violations/model';
import {LocalizedTitle, ViolationDictionaryItem, ViolationDto} from '@entities/violations/types';
import {Input} from '@shared/ui/Input';
import {StatusWrapper} from '@shared/ui/PageComponents';
import {Select, SelectOption} from '@shared/ui/Select';
import {mapTableColumns, Table} from '@shared/ui/Table2.0';
import {formatNumberWithFraction} from '@utils/number';

import {
    ControlsCard,
    SearchBox,
    SurMarker,
    TaxpayerCell,
    TaxpayerNameButton,
    TinText,
    Wrapper,
} from './styled';

const localizeTitle = (title: LocalizedTitle | null, language: string) => {
    if (!title) return EMPTY;
    return title[language] ?? title.en ?? Object.values(title)[0] ?? EMPTY;
};

const formatMoney = (value: number | null | undefined) =>
    value == null ? EMPTY : formatNumberWithFraction(value);

const formatTaxPeriod = (value: number | null | undefined) => {
    if (!value) return EMPTY;
    const raw = String(value);
    if (raw.length < 6) return raw;

    const year = Number(raw.slice(0, 4));
    const month = Number(raw.slice(4, 6)) - 1;
    if (Number.isNaN(year) || Number.isNaN(month) || month < 0 || month > 11) return raw;

    return format(new Date(year, month, 1), 'MMMM yyyy');
};

const violationStatusColor = (code?: string | null) =>
    code === 'CLOSED' ? STATUS_COLOR_LIST.GRAY_ALT : STATUS_COLOR_LIST.GREEN;

const notificationStatusColor = (code?: string | null) =>
    code === 'NEW'
        ? STATUS_COLOR_LIST.ORANGE
        : code === 'SENT'
        ? STATUS_COLOR_LIST.BLUE
        : code === 'SEND_ERROR'
        ? STATUS_COLOR_LIST.RED
        : code === 'READ'
        ? STATUS_COLOR_LIST.GREEN
        : STATUS_COLOR_LIST.GRAY;

const surColor = (sur?: number | null) =>
    sur === 3
        ? STATUS_COLOR_LIST.RED
        : sur === 2
        ? STATUS_COLOR_LIST.YELLOW
        : sur === 1
        ? STATUS_COLOR_LIST.GREEN
        : STATUS_COLOR_LIST.GRAY;

const getColumnsConfig = (
    t: (key: string, params?: Record<string, unknown>) => string,
    typeOptions: Array<SelectOption<string>>,
    violationStatusOptions: Array<SelectOption<string>>,
    notificationStatusOptions: Array<SelectOption<string>>,
) => [
    {
        id: 'taxpayer',
        header: t('page_audit.terms.taxpayer'),
        minWidth: 288,
    },
    {
        id: 'created',
        header: t('page_audit.terms.created'),
        minWidth: 134,
        isSortable: true,
    },
    {
        id: 'updated',
        header: t('page_audit.terms.updated'),
        minWidth: 134,
        isSortable: true,
    },
    {
        id: 'taxPeriod',
        header: t('page_audit.terms.tax-period'),
        minWidth: 120,
    },
    {
        id: 'source',
        header: t('page_audit.terms.violation-type'),
        minWidth: 231,
        filter: <Select isMulti options={typeOptions} closeMenuOnSelect={false} />,
    },
    {
        id: 'gapSum',
        header: t('page_audit.terms.tax-gap'),
        minWidth: 136,
        isSortable: true,
    },
    {
        id: 'violationStatus',
        header: t('page_audit.terms.violation-status'),
        minWidth: 160,
        filter: <Select isClearable options={violationStatusOptions} />,
    },
    {
        id: 'sendingStatus',
        header: t('page_audit.terms.notification-status'),
        minWidth: 170,
        filter: <Select isClearable options={notificationStatusOptions} />,
    },
];

export const AuditViolationsTab = () => {
    const {t, i18n} = useTranslation();
    const history = useHistory();
    const violations = useUnit(auditViolationsModel);

    useGate(AuditViolationsGate);

    const typeOptions = React.useMemo(
        () =>
            violations.state.violationTypes.map((item) => ({
                label: localizeTitle(item.title, i18n.language),
                value: item.id,
            })),
        [i18n.language, violations.state.violationTypes],
    );

    const violationStatusOptions = React.useMemo(
        () =>
            violations.state.violationStatuses.map((item) => ({
                label: localizeTitle(item.title, i18n.language),
                value: item.code,
            })),
        [i18n.language, violations.state.violationStatuses],
    );

    const notificationStatusOptions = React.useMemo(
        () =>
            violations.state.notificationStatuses.map((item) => ({
                label: localizeTitle(item.title, i18n.language),
                value: item.code,
            })),
        [i18n.language, violations.state.notificationStatuses],
    );

    const data = React.useMemo(
        () =>
            (violations.state.data?.data ?? []).map((row: ViolationDto) => {
                const violationType = violations.state.violationTypes.find(
                    (item: ViolationDictionaryItem) => item.id === row.type,
                );
                const notificationStatus = violations.state.notificationStatuses.find(
                    (item) =>
                        item.id === row.notificationStatus || item.code === row.notificationStatus,
                );
                const isClosed = row.status?.code === 'CLOSED';
                const mutedStyle = {opacity: isClosed ? 0.5 : 1};

                return {
                    ...row,
                    taxpayer: (
                        <TaxpayerCell style={mutedStyle}>
                            <SurMarker color={surColor(row.payerSur)} />
                            <div>
                                <TaxpayerNameButton
                                    type="button"
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        history.push(
                                            ROUTES.AUDIT_VIOLATIONS_TAXPAYER_DETAILS.replace(
                                                ':tin',
                                                row.payerTin,
                                            ),
                                        );
                                    }}
                                >
                                    {row.payerName || EMPTY}
                                </TaxpayerNameButton>
                                <TinText>
                                    {t('page_audit.terms.tin-no', {tin: row.payerTin})}
                                </TinText>
                            </div>
                        </TaxpayerCell>
                    ),
                    created: (
                        <div style={mutedStyle}>
                            <div>
                                {row.created ? format(new Date(row.created), 'dd.MM.yyyy') : EMPTY}
                            </div>
                            <div>{row.created ? format(new Date(row.created), 'HH:mm') : ''}</div>
                        </div>
                    ),
                    updated: (
                        <div style={mutedStyle}>
                            <div>
                                {row.updated ? format(new Date(row.updated), 'dd.MM.yyyy') : EMPTY}
                            </div>
                            <div>{row.updated ? format(new Date(row.updated), 'HH:mm') : ''}</div>
                        </div>
                    ),
                    taxPeriod: <div style={mutedStyle}>{formatTaxPeriod(row.taxPeriod)}</div>,
                    source: (
                        <div style={mutedStyle}>
                            {localizeTitle(violationType?.title ?? null, i18n.language)}
                        </div>
                    ),
                    gapSum: <div style={mutedStyle}>{formatMoney(row.gapSum)}</div>,
                    violationStatus: (
                        <div style={mutedStyle}>
                            <StatusWrapper color={violationStatusColor(row.status?.code)}>
                                {localizeTitle(row.status?.title ?? null, i18n.language)}
                            </StatusWrapper>
                        </div>
                    ),
                    sendingStatus: (
                        <div style={mutedStyle}>
                            <StatusWrapper
                                color={notificationStatusColor(notificationStatus?.code)}
                            >
                                {localizeTitle(notificationStatus?.title ?? null, i18n.language)}
                            </StatusWrapper>
                        </div>
                    ),
                };
            }),
        [
            history,
            i18n.language,
            t,
            violations.state.data,
            violations.state.notificationStatuses,
            violations.state.violationTypes,
        ],
    );

    const columns = React.useMemo(
        () =>
            mapTableColumns(
                getColumnsConfig(t, typeOptions, violationStatusOptions, notificationStatusOptions),
                violations.state.filters,
                violations.filtersChanged,
                violations.filtersReset,
                violations.state.sortings,
                violations.sortingsChanged,
            ),
        [
            notificationStatusOptions,
            t,
            typeOptions,
            violationStatusOptions,
            violations.filtersChanged,
            violations.filtersReset,
            violations.sortingsChanged,
            violations.state.filters,
            violations.state.sortings,
        ],
    );

    return (
        <Wrapper>
            <ControlsCard>
                <SearchBox>
                    <Input
                        size="sm"
                        value={violations.state.search}
                        placeholder={t('page_audit.page_violations.search_placeholder')}
                        onChange={(event) => violations.searchChanged(event.target.value)}
                    />
                </SearchBox>
            </ControlsCard>
            <Table
                columns={columns}
                data={data}
                isLoading={violations.state.pending}
                pageCount={Math.ceil(
                    (violations.state.data?.total ?? 0) / violations.state.pageSize,
                )}
                pageIndex={violations.state.pageNumber}
                totalRowsCount={violations.state.data?.total ?? 0}
                onPageChange={violations.pageNumberChanged}
                onPageSizeChange={violations.pageSizeChanged}
                onRowClick={(row) =>
                    history.push(
                        ROUTES.AUDIT_VIOLATION_GENERAL_INFORMATION.replace(':id', row.original.id),
                    )
                }
            />
        </Wrapper>
    );
};
